<template>
  <div class="min-h-screen bg-gray-50">
    <div class="bg-primary text-white py-16">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto text-center">
          <h1 class="text-4xl font-bold mb-4">Preise & Leistungen</h1>
          <p class="text-xl opacity-90">Transparente Preise für Privatkunden und individuelle Beratung für Unternehmen</p>
        </div>
      </div>
    </div>

    <!-- Tabs Navigation -->
    <div class="container mx-auto px-4 py-8">
      <div class="max-w-4xl mx-auto">
        <div class="flex justify-center mb-8">
          <button 
            @click="activeTab = 'private'"
            :class="['px-6 py-3 text-lg font-bold rounded-l-lg transition-colors flex items-center space-x-2', 
              activeTab === 'private' 
                ? 'bg-primary text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200']"
          >
            <UserIcon class="w-6 h-6" />
            <span>Privatkunden</span>
          </button>
          <button 
            @click="activeTab = 'business'"
            :class="['px-6 py-3 text-lg font-bold rounded-r-lg transition-colors flex items-center space-x-2', 
              activeTab === 'business' 
                ? 'bg-primary text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200']"
          >
            <BuildingOfficeIcon class="w-6 h-6" />
            <span>Geschäftskunden</span>
          </button>
        </div>

        <!-- Privatkunden Tab -->
        <div v-if="activeTab === 'private'" class="space-y-8">
          <!-- Stundensätze -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <h2 class="text-2xl font-bold text-primary mb-6">Stundensätze</h2>
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead>
                  <tr class="border-b-2 border-gray-200">
                    <th class="text-left py-3 px-4">Leistung</th>
                    <th class="text-left py-3 px-4">Beschreibung</th>
                    <th class="text-right py-3 px-4">Preis*</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="border-b border-gray-100">
                    <td class="py-3 px-4 font-medium">Assistent-Service</td>
                    <td class="py-3 px-4 text-gray-600">Remote-Support und Fernwartung (ohne Vor-Ort-Einsatz)</td>
                    <td class="py-3 px-4 text-right">49,00 €/Std</td>
                  </tr>
                  <tr>
                    <td class="py-3 px-4 font-medium">IT-Service vor Ort</td>
                    <td class="py-3 px-4 text-gray-600">Professioneller IT-Service bei Ihnen vor Ort zzgl. Anfahrt</td>
                    <td class="py-3 px-4 text-right">69,00 €/Std</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="mt-8 bg-gray-50 rounded-lg p-6">
              <h3 class="text-xl font-bold mb-4">Wichtige Hinweise zur Abrechnung:</h3>
              <ul class="space-y-3 text-gray-700">
                <li class="flex items-start">
                  <ClockIcon class="w-5 h-5 text-primary mt-1 mr-2 flex-shrink-0" />
                  <span>Die erste Stunde wird immer vollständig berechnet. Ab der zweiten Stunde erfolgt die Abrechnung im 30-Minuten-Takt.</span>
                </li>
                <li class="flex items-start">
                  <DocumentTextIcon class="w-5 h-5 text-primary mt-1 mr-2 flex-shrink-0" />
                  <span>Hardware und Verbrauchsmaterialien werden separat berechnet.</span>
                </li>
                <li class="flex items-start">
                  <UserGroupIcon class="w-5 h-5 text-primary mt-1 mr-2 flex-shrink-0" />
                  <span>Jedes Anliegen ist individuell und wird von uns auch so behandelt. Wir finden die beste Lösung für Ihre spezifische Situation.</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- Anfahrtskosten -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <h2 class="text-2xl font-bold text-primary mb-6">Anfahrtskosten</h2>
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead>
                  <tr class="border-b-2 border-gray-200">
                    <th class="text-left py-3 px-4">Zone</th>
                    <th class="text-left py-3 px-4">Entfernung</th>
                    <th class="text-right py-3 px-4">Pauschale*</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="border-b border-gray-100">
                    <td class="py-3 px-4 font-medium">Zone 0</td>
                    <td class="py-3 px-4 text-gray-600">Bis 10 km</td>
                    <td class="py-3 px-4 text-right">0,00 €</td>
                  </tr>
                  <tr class="border-b border-gray-100">
                    <td class="py-3 px-4 font-medium">Zone 10</td>
                    <td class="py-3 px-4 text-gray-600">10 bis 20 km</td>
                    <td class="py-3 px-4 text-right">15,00 €</td>
                  </tr>
                  <tr>
                    <td class="py-3 px-4 font-medium">Zone 20+</td>
                    <td class="py-3 px-4 text-gray-600">Ab 20 km</td>
                    <td class="py-3 px-4 text-right">15,00 € + 0,60 €/km</td>
                  </tr>
                </tbody>
              </table>
              <p class="text-sm text-gray-500 mt-4">* Alle Preise sind Endpreise. Gemäß § 19 UStG erheben wir keine Umsatzsteuer und weisen diese folglich auch nicht aus (Kleinunternehmerregelung).</p>
            </div>
          </div>

          <!-- Praxisbeispiele -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <h2 class="text-2xl font-bold text-primary mb-6">Praxisbeispiele aus unserem Alltag</h2>
            <div class="grid md:grid-cols-2 gap-6">
              <!-- Beispiel 1 -->
              <div class="border rounded-lg p-6">
                <h3 class="text-xl font-bold mb-3 flex items-center">
                  <WifiIcon class="w-6 h-6 text-primary mr-2" />
                  Router-Konfiguration
                </h3>
                <p class="text-gray-600 mb-4">Ein Kunde hatte nach einer selbst durchgeführten Router-Umstellung Verbindungsprobleme. Wir konnten das Problem innerhalb einer Stunde beheben.</p>
                <div class="bg-gray-50 p-4 rounded-lg">
                  <h4 class="font-bold mb-2">Abrechnung:</h4>
                  <ul class="space-y-1 text-gray-700">
                    <li>• IT-Service vor Ort: 69,00 €</li>
                    <li>• Anfahrt Zone 1: 15,00 €</li>
                    <li class="font-bold pt-2">Gesamtbetrag: 84,00 €</li>
                  </ul>
                </div>
              </div>

              <!-- Beispiel 2 -->
              <div class="border rounded-lg p-6">
                <h3 class="text-xl font-bold mb-3 flex items-center">
                  <PrinterIcon class="w-6 h-6 text-primary mr-2" />
                  Drucker & Netzwerk
                </h3>
                <p class="text-gray-600 mb-4">Ein Kunde hatte Probleme mit seinem Netzwerkdrucker und benötigte neue Patronen. Die Problembehebung dauerte 45 Minuten.</p>
                <div class="bg-gray-50 p-4 rounded-lg">
                  <h4 class="font-bold mb-2">Abrechnung:</h4>
                  <ul class="space-y-1 text-gray-700">
                    <li>• IT-Service vor Ort: 69,00 €</li>
                    <li>• Anfahrt Zone 1: 15,00 €</li>
                    <li>• Druckerpatrone: 29,00 €</li>
                    <li class="font-bold pt-2">Gesamtbetrag: 113,00 €</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Geschäftskunden Tab -->
        <div v-if="activeTab === 'business'" class="space-y-8">
          <!-- Intro -->
          <div class="bg-white rounded-lg shadow-md p-8 text-center">
            <h2 class="text-3xl font-bold text-primary mb-4">Maßgeschneiderte IT-Lösungen für Ihr Unternehmen</h2>
            <p class="text-lg text-gray-600 mb-6">Jedes Unternehmen hat individuelle IT-Anforderungen. Deshalb erstellen wir für Sie ein passgenaues Angebot, das exakt auf Ihre Bedürfnisse zugeschnitten ist.</p>
            <div class="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <p class="text-primary font-semibold text-lg">Persönliche Beratung - unverbindlich und auf Sie zugeschnitten</p>
              <p class="text-gray-600 mt-2">Lassen Sie uns gemeinsam die optimale IT-Strategie für Ihr Unternehmen entwickeln.</p>
            </div>
          </div>

          <!-- IT-Wartung & Betreuung -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <h2 class="text-2xl font-bold text-primary mb-6">IT-Wartung & Proaktive Betreuung</h2>
            <p class="text-lg text-gray-600 mb-6">Kontinuierliche Pflege und Überwachung Ihrer IT-Infrastruktur für maximale Betriebssicherheit</p>
            
            <div class="grid md:grid-cols-2 gap-8">
              <div>
                <h3 class="text-xl font-bold mb-4 flex items-center">
                  <ComputerDesktopIcon class="w-6 h-6 mr-2 text-primary" />
                  Systemwartung & Updates
                </h3>
                <ul class="space-y-3">
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Regelmäßige Pflege und Absicherung aller PCs und Server</span>
                  </li>
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Automatische Windows-Updates und Software-Aktualisierungen</span>
                  </li>
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Treiberpflege und Hardware-Kompatibilität</span>
                  </li>
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Kontinuierliches Monitoring der Systemgesundheit</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 class="text-xl font-bold mb-4 flex items-center">
                  <ShieldCheckIcon class="w-6 h-6 mr-2 text-primary" />
                  Sicherheit & Überwachung
                </h3>
                <ul class="space-y-3">
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Integration und Verwaltung von Antivirus-Lösungen</span>
                  </li>
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Hardware-Status und Speicherplatz-Überwachung</span>
                  </li>
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Proaktive Erkennung von Sicherheitsbedrohungen</span>
                  </li>
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Regelmäßige Backup-Kontrolle und Datensicherung</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Support & IT-Infrastruktur -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <h2 class="text-2xl font-bold text-primary mb-6">Support & IT-Infrastruktur</h2>
            <p class="text-lg text-gray-600 mb-6">Schneller, direkter IT-Support und professionelle Infrastrukturlösungen</p>
            
            <div class="grid md:grid-cols-2 gap-8">
              <div>
                <h3 class="text-xl font-bold mb-4 flex items-center">
                  <WrenchScrewdriverIcon class="w-6 h-6 mr-2 text-primary" />
                  IT-Support & Help-Desk
                </h3>
                <ul class="space-y-3">
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Schneller, direkter Support ohne Warteschleifen</span>
                  </li>
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Remote-Support für sofortige Problemlösung</span>
                  </li>
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Ein fester Ansprechpartner, der Ihr System kennt</span>
                  </li>
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Flexible Betreuung mit skalierbaren Verträgen</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 class="text-xl font-bold mb-4 flex items-center">
                  <ServerIcon class="w-6 h-6 mr-2 text-primary" />
                  Netzwerk & Infrastruktur
                </h3>
                <ul class="space-y-3">
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Netzwerkplanung und professionelle Implementierung</span>
                  </li>
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>WLAN-Ausleuchtung und Optimierung</span>
                  </li>
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>VPN-Lösungen für sicheres Remote-Arbeiten</span>
                  </li>
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Server-Lösungen und Virtualisierung</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Cloud & Microsoft 365 -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <h2 class="text-2xl font-bold text-primary mb-6">Cloud-Management & Microsoft 365</h2>
            <p class="text-lg text-gray-600 mb-6">Vollständige Verwaltung und Optimierung Ihrer Cloud-Umgebung</p>
            
            <div class="grid md:grid-cols-2 gap-8">
              <div>
                <h3 class="text-xl font-bold mb-4 flex items-center">
                  <CloudIcon class="w-6 h-6 mr-2 text-primary" />
                  Microsoft 365 Verwaltung
                </h3>
                <ul class="space-y-3">
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Komplette Verwaltung von Microsoft 365 Konten</span>
                  </li>
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>E-Mail, Teams und OneDrive Einrichtung</span>
                  </li>
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Benutzerverwaltung und Rechtevergabe</span>
                  </li>
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Schulungen für Mitarbeiter</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 class="text-xl font-bold mb-4 flex items-center">
                  <ShieldCheckIcon class="w-6 h-6 mr-2 text-primary" />
                  IT-Sicherheit & Backup
                </h3>
                <ul class="space-y-3">
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Automatisches Backup für alle sensiblen Daten</span>
                  </li>
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Endpoint-Protection und Firewall-Management</span>
                  </li>
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Regelmäßige Kontrolle der Datensicherung</span>
                  </li>
                  <li class="flex items-start">
                    <CheckCircleIcon class="w-5 h-5 text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>Schutz vor Viren, Ransomware und Phishing</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>



          <!-- Cybersecurity & Mitarbeiterschulungen -->
          <div class="bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 border-2 border-orange-200 rounded-xl p-8">
            <!-- Header mit Statistik -->
            <div class="text-center mb-8">
              <div class="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-500 to-orange-500 rounded-full mx-auto mb-6 shadow-lg">
                <ExclamationTriangleIcon class="w-10 h-10 text-white" />
              </div>
              
              <div class="inline-flex items-center bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                91% der Cyber-Angriffe entstehen durch menschliche Fehler
              </div>
              
              <h2 class="text-3xl font-bold text-gray-900 mb-3">Awareness-Kampagnen</h2>
              <p class="text-lg text-gray-700 max-w-2xl mx-auto">
                Systeme sind nie 100% sicher - aber <strong>Ihre Mitarbeiter</strong> sind der beste Schutz gegen Cyber-Bedrohungen
              </p>
            </div>

            <!-- Kompakte Übersicht -->
            <div class="bg-white rounded-xl p-6 shadow-lg border border-orange-200 mb-6">
              <div class="grid md:grid-cols-2 gap-8">
                <!-- Phishing-Awareness-Kampagne -->
                <div>
                  <div class="flex items-center mb-4">
                    <div class="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mr-4">
                      <ShieldCheckIcon class="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 class="text-xl font-bold text-gray-900">Phishing-Awareness-Kampagne</h3>
                      <div class="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold mt-1">
                        ab 499€
                      </div>
                    </div>
                  </div>
                  
                  <ul class="space-y-2 text-gray-700 mb-4">
                    <li class="flex items-center">
                      <CheckCircleIcon class="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      <span>Realistische Phishing-Tests für Ihre Mitarbeiter</span>
                    </li>
                    <li class="flex items-center">
                      <CheckCircleIcon class="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      <span>Detaillierte Auswertung & Sensibilisierung</span>
                    </li>
                    <li class="flex items-center">
                      <CheckCircleIcon class="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      <span>Analyse der Schwachstellen</span>
                    </li>
                  </ul>
                </div>

                <!-- Zusätzliche Schulungen -->
                <div>
                  <div class="flex items-center mb-4">
                    <div class="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mr-4">
                      <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                      </svg>
                    </div>
                    <div>
                      <h3 class="text-xl font-bold text-gray-900">Zusätzliche Leistungen</h3>
                      <div class="text-gray-600 text-sm font-medium mt-1">Preise auf Anfrage</div>
                    </div>
                  </div>
                  
                  <ul class="space-y-2 text-gray-700">
                    <li class="flex items-center">
                      <CheckCircleIcon class="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                      <span>Individuelle Mitarbeiterschulungen</span>
                    </li>
                    <li class="flex items-center">
                      <CheckCircleIcon class="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                      <span>DSGVO & Datenschutz-Training</span>
                    </li>
                    <li class="flex items-center">
                      <CheckCircleIcon class="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                      <span>Sichere Passwort-Strategien</span>
                    </li>
                    <li class="flex items-center">
                      <CheckCircleIcon class="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                      <span>Maßgeschneiderte Schulungskonzepte</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Call-to-Action -->
            <div class="text-center">
              <p class="text-gray-600 mb-4">
                <strong>Stärken Sie Ihre erste Verteidigungslinie:</strong> Gut geschulte Mitarbeiter sind Ihr bester Schutz vor Cyber-Angriffen
              </p>
            </div>
          </div>


        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  UserGroupIcon,
  UserIcon,
  BuildingOfficeIcon,
  ServerIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
  WifiIcon,
  PrinterIcon,
  ComputerDesktopIcon,
  CloudIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline'

export default {
  name: 'Pricing',
  components: {
    CheckCircleIcon,
    ClockIcon,
    DocumentTextIcon,
    UserGroupIcon,
    UserIcon,
    BuildingOfficeIcon,
    ServerIcon,
    ShieldCheckIcon,
    WrenchScrewdriverIcon,
    WifiIcon,
    PrinterIcon,
    ComputerDesktopIcon,
    CloudIcon,
    ExclamationTriangleIcon
  },
  data() {
    return {
      activeTab: 'private'
    }
  }
}
</script>

<style scoped>
.pricing-card {
  @apply relative rounded-lg p-6 transition-all duration-300;
  height: 100%;
}

.pricing-card:hover {
  @apply shadow-lg transform -translate-y-1;
}
</style> 